import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { usePathname } from './usePathname';
import { useQueryParams } from './useQueryParams';

type StateResult<T> = {
  value: T;
  set: (value: T) => void;
  clear: () => void;
};

export function useQueryParam<T extends string>({
  fallbackValue,
  key,
}: {
  key: string;
  fallbackValue?: T;
}): StateResult<T> {
  const router = useRouter();
  const pathname = usePathname();
  const query = useQueryParams();

  const value =
    typeof query[key] === 'string'
      ? decodeURIComponent(query[key])
      : fallbackValue;

  const set = useCallback(
    (v: T) => {
      if (v === '') {
        clear();
        return;
      }

      const url = {
        pathname,
        query: {
          ...query,
          [key]: encodeURIComponent(v),
        },
      };

      router.push(url, undefined, { shallow: true });
    },
    [router, pathname, query],
  );

  const clear = useCallback(() => {
    const newQuery = { ...query };
    delete newQuery[key as keyof typeof newQuery];

    const url = {
      pathname,
      query: newQuery,
    };

    router.push(url, undefined, { shallow: true });
  }, [router, pathname, query]);

  return {
    value: value as T,
    set,
    clear,
  };
}
